export const SMS_DEFAULT_PRICE_DKK_CENTS = 25;

const GSM7_BASIC =
  "@£$¥èéùìòÇ\nØø\rÅåΔ_ΦΓΛΩΠΨΣΘΞ\x1bÆæßÉ !\"#¤%&'()*+,-./0123456789:;<=>?¡ABCDEFGHIJKLMNOPQRSTUVWXYZÄÖÑÜ§¿abcdefghijklmnopqrstuvwxyzäöñüà";
const GSM7_EXT = "\f^{}\\[~]|€";
const GSM7_BASIC_SET = new Set(GSM7_BASIC);
const GSM7_EXT_SET = new Set(GSM7_EXT);

export type SmsEncoding = 'gsm7' | 'ucs2';

export type SmsBodyAnalysis = {
  encoding: SmsEncoding;
  segments: number;
  charactersUsed: number;
};

/** GSM-7 / UCS-2 segment count — same rules as the GatewayAPI send path. */
export function analyzeSmsBody(body: string): SmsBodyAnalysis {
  let needsUcs2 = false;
  let charactersUsed = 0;

  for (const ch of body) {
    if (GSM7_BASIC_SET.has(ch)) {
      charactersUsed += 1;
    } else if (GSM7_EXT_SET.has(ch)) {
      charactersUsed += 2;
    } else {
      needsUcs2 = true;
      break;
    }
  }

  if (needsUcs2) {
    const len = [...body].length;
    if (len <= 70) return { encoding: 'ucs2', segments: 1, charactersUsed: len };
    return { encoding: 'ucs2', segments: Math.ceil(len / 67), charactersUsed: len };
  }

  if (charactersUsed <= 160) {
    return { encoding: 'gsm7', segments: Math.max(charactersUsed === 0 ? 0 : 1, 0), charactersUsed };
  }
  return {
    encoding: 'gsm7',
    segments: Math.ceil(charactersUsed / 153),
    charactersUsed,
  };
}

export function smsSegmentCount(body: string): number {
  if (body.length === 0) return 0;
  return analyzeSmsBody(body).segments;
}

export function smsPriceCents(segments: number, pricePerSegmentCents?: number | null): number {
  const unit =
    typeof pricePerSegmentCents === 'number' && pricePerSegmentCents > 0
      ? pricePerSegmentCents
      : SMS_DEFAULT_PRICE_DKK_CENTS;
  return segments * unit;
}

export function smsPriceDkk(segments: number, clinicPriceCents?: number | null): string {
  const cents = clinicPriceCents ?? smsPriceCents(segments);
  return (cents / 100).toLocaleString('da-DK', {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });
}

export type SmsSendBlockReason = 'disabled' | 'no_phone' | 'no_consent' | 'empty' | 'no_credit';

export function patientSmsSendBlockReason(opts: {
  body: string;
  phone: string;
  consentAccepted: boolean;
  smsEnabled: boolean;
  hasCredit?: boolean;
}): SmsSendBlockReason | null {
  if (!opts.smsEnabled) return 'disabled';
  if (!opts.phone.trim()) return 'no_phone';
  if (!opts.consentAccepted) return 'no_consent';
  if (opts.hasCredit === false) return 'no_credit';
  if (!opts.body.trim()) return 'empty';
  return null;
}

export function smsTriggerLabelKey(trigger: string | null | undefined): {
  key: string;
  fallback: string;
} {
  switch (trigger) {
    case 'manual':
      return { key: 'sms.trigger_manual', fallback: 'Manuel' };
    case 'reminder_3d':
      return { key: 'sms.trigger_reminder_3d', fallback: 'Påmindelse · 3 dage' };
    case 'reminder_1d':
      return { key: 'sms.trigger_reminder_1d', fallback: 'Påmindelse · 1 dag' };
    case 'reminder_2h':
      return { key: 'sms.trigger_reminder_2h', fallback: 'Påmindelse · 2 timer' };
    case 'reminder_custom':
      return { key: 'sms.trigger_reminder_custom', fallback: 'Påmindelse' };
    case 'followup_1d':
      return { key: 'sms.trigger_followup_1d', fallback: 'Opfølgning · 1 dag' };
    case 'followup_1w':
      return { key: 'sms.trigger_followup_1w', fallback: 'Opfølgning · 1 uge' };
    case 'followup_1m':
      return { key: 'sms.trigger_followup_1m', fallback: 'Opfølgning · 1 måned' };
    case 'followup_custom':
      return { key: 'sms.trigger_followup_custom', fallback: 'Opfølgning' };
    case 'confirmation_booked':
      return { key: 'sms.trigger_confirmation_booked', fallback: 'Bekræftelse · Booket' };
    case 'confirmation_cancelled':
      return { key: 'sms.trigger_confirmation_cancelled', fallback: 'Bekræftelse · Aflyst' };
    case 'confirmation_rescheduled':
      return { key: 'sms.trigger_confirmation_rescheduled', fallback: 'Bekræftelse · Flyttet' };
    case 'consent_request':
      return { key: 'sms.trigger_consent_request', fallback: 'Samtykke-anmodning' };
    case 'consent_portal_request':
      return { key: 'sms.trigger_consent_portal_request', fallback: 'Samtykke i portalen' };
    case 'test':
      return { key: 'sms.trigger_test', fallback: 'Test' };
    case 'confirmation':
      return { key: 'sms.trigger_confirmation', fallback: 'Bekræftelse' };
    case 'reminder':
      return { key: 'sms.trigger_reminder', fallback: 'Påmindelse' };
    case 'followup':
      return { key: 'sms.trigger_followup', fallback: 'Opfølgning' };
    default:
      return { key: '', fallback: trigger || '—' };
  }
}

export function smsStatusLabelKey(status: string | null | undefined): {
  key: string;
  fallback: string;
} {
  switch (status) {
    case 'all':
      return { key: 'sms.status_all', fallback: 'Alle' };
    case 'queued':
      return { key: 'sms.status_queued', fallback: 'I kø' };
    case 'sent':
      return { key: 'sms.status_sent', fallback: 'Sendt' };
    case 'delivered':
      return { key: 'sms.status_delivered', fallback: 'Leveret' };
    case 'failed':
      return { key: 'sms.status_failed', fallback: 'Fejlet' };
    case 'rejected':
      return { key: 'sms.status_rejected', fallback: 'Afvist' };
    case 'expired':
      return { key: 'sms.status_expired', fallback: 'Udløbet' };
    case 'unknown':
      return { key: 'sms.status_unknown', fallback: 'Ukendt' };
    case 'pending':
      return { key: 'sms.status_pending', fallback: 'Afventer' };
    case 'skipped':
      return { key: 'sms.status_skipped', fallback: 'Sprunget over' };
    default:
      return { key: '', fallback: status || '—' };
  }
}

export function smsReasonLabelKey(reason: string | null | undefined): {
  key: string;
  fallback: string;
} {
  switch (reason) {
    case null:
    case undefined:
    case '':
      return { key: '', fallback: '—' };
    case 'no_consent':
      return { key: 'sms.reason_no_consent', fallback: 'Intet samtykke' };
    case 'no_patient':
      return { key: 'sms.reason_no_patient', fallback: 'Ingen patient' };
    case 'no_phone':
      return { key: 'sms.reason_no_phone', fallback: 'Intet telefonnummer' };
    case 'invalid_phone':
      return { key: 'sms.reason_invalid_phone', fallback: 'Ugyldigt telefonnummer' };
    case 'sms_disabled':
      return { key: 'sms.reason_sms_disabled', fallback: 'SMS er slået fra' };
    case 'feature_off':
      return { key: 'sms.reason_feature_off', fallback: 'Funktion slået fra' };
    case 'sms_reminder_opt_in':
      return { key: 'sms.reason_reminder_opt_in', fallback: 'Påmindelse er ikke slået til' };
    case 'stale':
      return { key: 'sms.reason_stale', fallback: 'Forældet' };
    case 'already_sent':
      return { key: 'sms.reason_already_sent', fallback: 'Allerede sendt' };
    case 'insufficient_credits':
      return { key: 'sms.reason_insufficient_credits', fallback: 'Ikke nok kredit' };
    case 'appointment_cancelled':
      return { key: 'sms.reason_appointment_cancelled', fallback: 'Aftale aflyst' };
    case 'config_missing':
      return { key: 'sms.reason_config_missing', fallback: 'Udbyder ikke konfigureret' };
    case 'gateway_error':
      return { key: 'sms.reason_gateway_error', fallback: 'Udbyderfejl' };
    default:
      return { key: '', fallback: reason };
  }
}

export function remainingSmsSegments(balanceCents: number, pricePerSegmentCents?: number | null): number {
  const unit =
    typeof pricePerSegmentCents === 'number' && pricePerSegmentCents > 0
      ? pricePerSegmentCents
      : SMS_DEFAULT_PRICE_DKK_CENTS;
  if (balanceCents <= 0) return 0;
  return Math.floor(balanceCents / unit);
}
